import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Globe, TrendingUp, Users, Megaphone, Cpu, Lightbulb, Loader2,
  RefreshCw, ArrowRight, Clock, Target, BarChart3, Beaker,
  ChevronRight, Calendar, Star, Zap, FileText,
} from "lucide-react";

const CAT_CONFIG = {
  market: { label: "Thị trường", icon: TrendingUp, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  customer_behavior: { label: "Hành vi KH", icon: Users, color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/20" },
  content_social: { label: "Content/Social", icon: Megaphone, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/20" },
  competitor: { label: "Đối thủ", icon: Target, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" },
  ai_tech: { label: "AI & Tech", icon: Cpu, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  product_opportunity: { label: "Cơ hội SP", icon: Lightbulb, color: "text-lime-400", bg: "bg-lime-500/10 border-lime-500/20" },
};

const SUB_TABS = [
  { key: "daily", label: "Báo cáo ngày", icon: FileText },
  { key: "weekly", label: "Tổng hợp tuần", icon: Calendar },
  { key: "trends", label: "Trends", icon: TrendingUp },
  { key: "opportunities", label: "Cơ hội", icon: Star },
];

export default function ResearchPanel() {
  const [subTab, setSubTab] = useState("daily");

  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="research-panel">
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
              subTab === t.key ? "bg-[#1e293b] text-lime-400" : "text-slate-500 hover:text-slate-300"
            }`} data-testid={`research-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {subTab === "daily" && <DailyReportTab />}
        {subTab === "weekly" && <WeeklySummaryTab />}
        {subTab === "trends" && <TrendsTab />}
        {subTab === "opportunities" && <OpportunitiesTab />}
      </div>
    </div>
  );
}

// ==================== DAILY REPORT ====================
function DailyReportTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const endpoint = force ? "/research/daily-report/refresh" : "/research/daily-report";
      const method = force ? "post" : "get";
      const { data } = await api[method](endpoint);
      setReport(data);
    } catch { toast.error("Lỗi tải báo cáo"); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toAction = async (trendId, type) => {
    try {
      const { data } = await api.post("/research/insight-to-action", { trend_id: trendId, action_type: type });
      toast.success(data.message);
    } catch { toast.error("Lỗi"); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="daily-report">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Globe className="w-5 h-5 text-lime-400" /> AI Research Daily Report</h2>
          <p className="text-xs text-slate-500 mt-0.5">{report?.date || ""} — {report?.total_insights || 0} insights</p>
        </div>
        <Button onClick={() => fetch(true)} disabled={refreshing} variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-lime-400 gap-1.5 text-xs" data-testid="refresh-report-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "AI đang nghiên cứu..." : "Làm mới"}
        </Button>
      </div>

      {/* Summary + Top Action */}
      {report?.summary && (
        <div className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-4">
          <p className="text-xs text-slate-300">{report.summary}</p>
          {report.top_action && (
            <div className="flex items-center gap-2 mt-2 bg-[#0f172a] rounded-lg p-2">
              <Zap className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <p className="text-xs text-amber-400 font-medium">{report.top_action}</p>
            </div>
          )}
        </div>
      )}

      {/* Insights by category */}
      {report?.insights?.length > 0 && (
        <div className="space-y-2">
          {report.insights.map((insight, i) => {
            const cat = CAT_CONFIG[insight.category] || CAT_CONFIG.market;
            const CatIcon = cat.icon;
            return (
              <div key={i} className={`border rounded-xl p-4 ${cat.bg}`} data-testid={`insight-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <CatIcon className={`w-4 h-4 ${cat.color} shrink-0`} />
                    <h3 className="text-sm font-medium text-slate-200">{insight.title}</h3>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Badge className={`text-[8px] border-0 ${insight.priority === "high" ? "bg-red-500/20 text-red-400" : insight.priority === "medium" ? "bg-amber-500/20 text-amber-400" : "bg-slate-500/20 text-slate-400"}`}>{insight.priority}</Badge>
                    {insight.roi_score > 0 && <Badge className="bg-lime-500/20 text-lime-400 text-[8px] border-0">ROI {insight.roi_score}</Badge>}
                  </div>
                </div>

                <p className="text-xs text-slate-400 mb-1.5">{insight.insight}</p>
                <p className="text-[10px] text-slate-500 mb-1"><span className="text-slate-600">Tại sao quan trọng:</span> {insight.why_it_matters}</p>
                <p className="text-[10px] text-lime-400/80"><span className="text-slate-600">Hành động:</span> {insight.recommended_action}</p>

                {/* Experiment */}
                {insight.experiment && insight.experiment.hypothesis && (
                  <details className="mt-2 group">
                    <summary className="text-[10px] text-purple-400 cursor-pointer hover:text-purple-300 flex items-center gap-1">
                      <Beaker className="w-3 h-3" /> Experiment Mode
                    </summary>
                    <div className="mt-1.5 bg-[#0f172a]/50 rounded-lg p-2.5 space-y-1">
                      <p className="text-[10px] text-slate-400"><span className="text-slate-600">Giả thuyết:</span> {insight.experiment.hypothesis}</p>
                      <p className="text-[10px] text-slate-400"><span className="text-slate-600">Cách test:</span> {insight.experiment.test_method}</p>
                      <p className="text-[10px] text-slate-400"><span className="text-slate-600">Kỳ vọng:</span> {insight.experiment.expected_outcome}</p>
                      <Badge className={`text-[8px] border-0 ${insight.experiment.effort === "low" ? "bg-lime-500/20 text-lime-400" : "bg-amber-500/20 text-amber-400"}`}>Effort: {insight.experiment.effort}</Badge>
                    </div>
                  </details>
                )}

                {/* Action buttons */}
                <div className="flex gap-1.5 mt-2">
                  {["campaign", "task", "content"].map(type => (
                    <button key={type} onClick={() => insight.id ? toAction(insight.id, type) : toast.info("Insight chưa có ID")}
                      className="text-[9px] text-slate-500 hover:text-lime-400 bg-[#0f172a] px-2 py-0.5 rounded transition-colors">
                      → {type === "campaign" ? "Chiến dịch" : type === "task" ? "Task" : "Content"}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== WEEKLY SUMMARY ====================
function WeeklySummaryTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const endpoint = force ? "/research/weekly-summary/refresh" : "/research/weekly-summary";
      const method = force ? "post" : "get";
      const { data } = await api[method](endpoint);
      setSummary(data);
    } catch { toast.error("Lỗi"); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="weekly-summary">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Calendar className="w-5 h-5 text-lime-400" /> Tổng hợp tuần {summary?.week}</h2>
        <Button onClick={() => fetch(true)} disabled={refreshing} variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-lime-400 gap-1.5 text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Làm mới
        </Button>
      </div>

      {summary?.summary && <p className="text-xs text-slate-400 bg-[#1e293b] rounded-xl p-4 border border-[#334155]">{summary.summary}</p>}

      {/* Top Trends */}
      {summary?.top_trends?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-lime-400 font-medium mb-2 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Top Trends</p>
          {summary.top_trends.map((t, i) => <p key={i} className="text-xs text-slate-300 mt-1">• {t}</p>)}
        </div>
      )}

      {/* Opportunities */}
      {summary?.best_opportunities?.length > 0 && (
        <div className="bg-[#1e293b] border border-lime-500/20 rounded-xl p-4">
          <p className="text-xs text-lime-400 font-medium mb-2 flex items-center gap-1"><Star className="w-3.5 h-3.5" /> Cơ hội tốt nhất</p>
          {summary.best_opportunities.map((o, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
              <div><p className="text-xs text-slate-200">{o.title}</p><p className="text-[10px] text-slate-500">{o.action}</p></div>
              {o.roi_score > 0 && <Badge className="bg-lime-500/20 text-lime-400 text-[8px] border-0">ROI {o.roi_score}</Badge>}
            </div>
          ))}
        </div>
      )}

      {/* Experiments */}
      {summary?.experiments?.length > 0 && (
        <div className="bg-[#1e293b] border border-purple-500/20 rounded-xl p-4">
          <p className="text-xs text-purple-400 font-medium mb-2 flex items-center gap-1"><Beaker className="w-3.5 h-3.5" /> Experiments đề xuất</p>
          {summary.experiments.map((e, i) => (
            <div key={i} className="bg-[#0f172a] rounded-lg p-3 mt-2">
              <p className="text-xs text-slate-200 font-medium">{e.name}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{e.hypothesis}</p>
              <div className="flex gap-3 mt-1 text-[10px]">
                <span className="text-slate-500">Cách test: <span className="text-slate-400">{e.method}</span></span>
                <Badge className={`text-[8px] border-0 ${e.effort === "low" ? "bg-lime-500/20 text-lime-400" : "bg-amber-500/20 text-amber-400"}`}>{e.effort}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Next week focus */}
      {summary?.next_week_focus?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-amber-400 font-medium mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> Focus tuần tới</p>
          {summary.next_week_focus.map((f, i) => <p key={i} className="text-xs text-slate-300 mt-1">• {f}</p>)}
        </div>
      )}
    </div>
  );
}

// ==================== TRENDS ====================
function TrendsTab() {
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const p = filter ? `?category=${filter}` : "";
    api.get(`/research/trends${p}`).then(r => { setTrends(r.data.trends || []); setLoading(false); }).catch(() => setLoading(false));
  }, [filter]);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="trends-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-lime-400" /> Trend Database</h2>
      </div>
      <div className="flex gap-1 flex-wrap">
        <button onClick={() => setFilter("")} className={`px-2 py-1 rounded text-[10px] ${!filter ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>Tất cả</button>
        {Object.entries(CAT_CONFIG).map(([k, v]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-2 py-1 rounded text-[10px] ${filter === k ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>{v.label}</button>
        ))}
      </div>

      {loading ? <Loader2 className="w-6 h-6 text-lime-400 animate-spin mx-auto mt-8" /> :
      trends.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có trends. Tạo Daily Report trước.</p> :
      <div className="space-y-2">
        {trends.map((t, i) => {
          const cat = CAT_CONFIG[t.category] || CAT_CONFIG.market;
          return (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 flex items-start gap-3">
              <div className={`w-8 h-8 rounded-lg ${cat.bg} flex items-center justify-center shrink-0`}>
                <cat.icon className={`w-4 h-4 ${cat.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-200">{t.title}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{t.insight}</p>
                <div className="flex gap-2 mt-1">
                  <Badge className={`text-[8px] border-0 ${t.priority === "high" ? "bg-red-500/20 text-red-400" : "bg-slate-500/20 text-slate-400"}`}>{t.priority}</Badge>
                  {t.roi_score > 0 && <Badge className="bg-lime-500/20 text-lime-400 text-[8px] border-0">ROI {t.roi_score}</Badge>}
                  <Badge className="bg-[#0f172a] text-slate-500 text-[8px] border-0">{t.impact_area}</Badge>
                </div>
              </div>
            </div>
          );
        })}
      </div>}
    </div>
  );
}

// ==================== OPPORTUNITIES ====================
function OpportunitiesTab() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/research/opportunities").then(r => { setOpps(r.data.opportunities || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toAction = async (id, type) => {
    try { await api.post("/research/insight-to-action", { trend_id: id, action_type: type }); toast.success("Đã tạo draft"); }
    catch { toast.error("Lỗi"); }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="opportunities-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Star className="w-5 h-5 text-amber-400" /> Cơ hội cao nhất</h2>

      {loading ? <Loader2 className="w-6 h-6 text-lime-400 animate-spin mx-auto mt-8" /> :
      opps.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có cơ hội high-priority. Tạo Daily Report để phát hiện.</p> :
      <div className="space-y-3">
        {opps.map((o, i) => (
          <div key={i} className="bg-[#1e293b] border border-lime-500/20 rounded-xl p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-sm font-medium text-slate-200">{o.title}</h3>
              <Badge className="bg-lime-500/20 text-lime-400 text-[9px] border-0">ROI {o.roi_score}</Badge>
            </div>
            <p className="text-xs text-slate-400">{o.insight}</p>
            <p className="text-[10px] text-lime-400/80 mt-1">{o.recommended_action}</p>

            {o.experiment && o.experiment.hypothesis && (
              <div className="mt-2 bg-[#0f172a] rounded-lg p-2.5">
                <p className="text-[9px] text-purple-400 font-medium mb-1">Experiment</p>
                <p className="text-[10px] text-slate-400">{o.experiment.hypothesis}</p>
              </div>
            )}

            <div className="flex gap-1.5 mt-3">
              <Button onClick={() => toAction(o.id, "campaign")} size="sm" className="bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/20 h-6 text-[9px] gap-1">
                <Megaphone className="w-3 h-3" /> Chiến dịch
              </Button>
              <Button onClick={() => toAction(o.id, "task")} size="sm" className="bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 border border-sky-500/20 h-6 text-[9px] gap-1">
                <Target className="w-3 h-3" /> Task
              </Button>
              <Button onClick={() => toAction(o.id, "content")} size="sm" className="bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 h-6 text-[9px] gap-1">
                <FileText className="w-3 h-3" /> Content
              </Button>
            </div>
          </div>
        ))}
      </div>}
    </div>
  );
}
